/**
 * Central navigation helper that integrates with AI Assistant
 * All navigation actions should use this helper to ensure consistent behavior
 */

export type NavigationTarget =
  | 'dashboard'
  | 'connecties'
  | 'agenda'
  | 'inbox'
  | 'mijn-verhaal'
  | 'vibes'
  | 'chat'
  | 'social-media'
  | 'training'
  | 'content'
  | 'membership'
  | 'cards-editor';

/**
 * Navigate to a page using hash-based routing
 *
 * @param target - The page/section to navigate to
 */
export function navigateTo(target: NavigationTarget): void {
  console.log('NavigationHelper: Navigating to:', target);

  // Update hash for hash-based routing
  window.location.hash = target;
}

/**
 * Open a modal or special view
 * This dispatches a custom event that components can listen to
 *
 * @param action - The action to perform
 * @param detail - Additional data for the action
 */
export function triggerAction(action: string, detail?: any): void {
  console.log('NavigationHelper: Triggering action:', action, detail);

  window.dispatchEvent(new CustomEvent(action, { detail }));
}

/**
 * Open the newsfeed
 */
export function openNewsfeed(): void {
  console.log('NavigationHelper: Opening newsfeed');

  // First navigate to dashboard
  navigateTo('dashboard');

  // Then open the newsfeed
  setTimeout(() => {
    triggerAction('openNewsfeed');
  }, 100);
}

/**
 * Open a specific inbox tab
 */
export function openInboxTab(tab: string): void {
  console.log('NavigationHelper: Opening inbox tab:', tab);

  // Navigate to inbox
  navigateTo('inbox');

  // Open specific tab
  setTimeout(() => {
    triggerAction('openInboxTab', { tab });
  }, 100);
}

/**
 * Open ConnectieFlow for a specific connection
 */
export function openConnectieFlow(connectionId?: string, mode?: 'chat' | 'agenda'): void {
  console.log('NavigationHelper: Opening ConnectieFlow', connectionId ? `for: ${connectionId}` : '', mode ?? 'no-mode');

  // Navigate to connecties
  navigateTo('connecties');

  // Open flow for specific connection if provided
  if (connectionId) {
    setTimeout(() => {
      // Only include mode when explicitly set - omitting it keeps the connection's current phase intact
      const detail: Record<string, unknown> = { connectionId };
      if (mode) detail.mode = mode;
      triggerAction('openConnectionFlowForConnection', detail);
    }, 100);
  } else {
    // Just open the ConnectieFlow
    setTimeout(() => {
      triggerAction('openConnectieFlow');
    }, 100);
  }
}

/**
 * Navigate to agenda page
 */
export function openAgenda(): void {
  console.log('NavigationHelper: Opening agenda');
  navigateTo('agenda');
}

/**
 * Navigate to chat
 */
export function openChat(): void {
  console.log('NavigationHelper: Opening chat');
  navigateTo('chat');
}
